import {
  CheckboxQuestionCompareCrosstabResponse,
  DisplayOptionsForQuestion,
  GetQuestionCompareCheckboxResultsArgs,
  MultiScoreResults,
  MultiScoreResultsArgs,
  QuestionAnswerChoice,
  QuestionComment,
  QuestionDeployRequest,
  QuestionDeployTarget,
  QuestionDisplayOption,
  QuestionEditRequest,
  QuestionFull,
  QuestionInsightListResponse,
  QuestionInsightNarrativeResponse,
  QuestionNotification,
  QuestionNotificationsRequest,
  QuestionResultsFilters,
  QuestionSample,
  QuestionSearchArgs,
  QuestionTagListResponse,
  QuestionTimeviewStatus,
  QuestionTranslation,
  SearchQuestionsResponse,
} from "types";
import {
  QueryClient,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { cacheKeyForDateRange, defaultChartQueryOptions } from "utils";

import { QuestionsAPI } from "api";
import { invalidateQuestionResults } from "components/questions/results/useQuestionResults";
import { mapBy } from "@civicscience/chops";

type QuestionInsightsListArgs = Parameters<typeof QuestionsAPI.getInsightList>[0];

type QuestionInsightsNarrativeArgs = Parameters<typeof QuestionsAPI.getInsightNarrative>[0];

type useQuestionCompareCheckboxResultsArgs = {
  qid: string | number;
  checkboxGroupId: string | number;
  filters: QuestionResultsFilters;
};

type QuestionTranslationArgs = {
  id: number | string;
  locale: string;
};

export const queryKeys = {
  all: () => ["questions"],
  details: () => [...queryKeys.all(), "detail"] as const,
  detail: (id: number | string) => [...queryKeys.details(), id] as const,
  answerChoices: (id: number | string) => [...queryKeys.detail(id), "answer-choices"] as const,
  answerDisplayGroupings: (id: number | string) => [...queryKeys.detail(id), "answer-display-groupings"] as const,
  scores: (id: number | string) => [...queryKeys.detail(id), "scores"],
  answerDisplayOptions: (id: number | string) => [...queryKeys.detail(id), "display-options"] as const,
  notifications: (id: number | string) => [...queryKeys.detail(id), "notifications"] as const,
  scoreResults: ({ questionId, startDate, endDate, ...args }: MultiScoreResultsArgs) => [
    ...queryKeys.detail(questionId),
    "score-results",
    {
      ...args,
      ...cacheKeyForDateRange({ startDate, endDate }),
    },
  ],
  timeviewStatus: (id: number | string) => [...queryKeys.detail(id), "timeview-status"],
  translation: ({ id, locale }: QuestionTranslationArgs) => [...queryKeys.detail(id), "translation", locale],
  translations: (id: number | string) => [...queryKeys.detail(id), "translations"],
  insightsList: ({ questionId, startDate = null, endDate = null, ...args }: QuestionInsightsListArgs) => [
    ...queryKeys.detail(questionId),
    "question-insights",
    {
      mode: args.mode,
      segmentUUID: args.segmentUUID,
      targetId: args.targetId,
      network: args.network,
      valueGroupingUri: args.valueGroupingUri,
      weightingSchemeName: args.weightingSchemeName,
      ...cacheKeyForDateRange({ startDate, endDate }),
    },
  ],
  insightsNarrative: ({ questionId, startDate = null, endDate = null, ...args }: QuestionInsightsNarrativeArgs) => [
    ...queryKeys.detail(questionId),
    "question-insights-narrative",
    {
      mode: args.mode,
      segmentUUID: args.segmentUUID,
      targetId: args.targetId,
      network: args.network,
      weightingSchemeName: args.weightingSchemeName,
      ...cacheKeyForDateRange({ startDate, endDate }),
    },
  ],
  searchBase: () => ["question-search"],
  search: ({ startDate = null, endDate = null, ...params }: QuestionSearchArgs) => [
    ...queryKeys.searchBase(),
    {
      searchAnswerOptions: params.searchAnswerOptions,
      includeScores: params.includeScores,
      inputType: params.inputType,
      isArchived: params.isArchived,
      isCurrentlyLive: params.isCurrentlyLive,
      isCyclical: params.isCyclical,
      isEngagement: params.isEngagement,
      isMyFavoritesOnly: params.isMyFavoritesOnly,
      isProfile: params.isProfile,
      isTracking: params.isTracking,
      isTrending: params.isTrending,
      isValue: params.isValue,
      order: params.order,
      orderBy: params.orderBy,
      page: params.page,
      query: params.query,
      questionType: params.questionType,
      responseCountGte: params.responseCountGte,
      size: params.size,
      tags: params.tags,
      taxonomyTags: params.taxonomyTags,
      categories: params.categories,
      ...cacheKeyForDateRange({ startDate, endDate }),
    },
  ],
  targetList: (questionId: number) => [...queryKeys.all(), "target-list", { questionId }] as const,
  tags: (questionId: number | string) => [...queryKeys.detail(questionId), "tags"] as const,
  comments: (questionId: number | string) => [...queryKeys.detail(questionId), "comments"] as const,
};

/**
 * Fetches the answer choices for a question.
 */
export const useQuestionAnswerChoices = <T = QuestionAnswerChoice[]>(
  id: number | string,
  options: UseQueryOptions<QuestionAnswerChoice[], unknown, T> = {},
) =>
  useQuery<QuestionAnswerChoice[], unknown, T>(
    queryKeys.answerChoices(id),
    () => QuestionsAPI.listAnswerChoices(id),
    options,
  );

/**
 * Fetches all display groupings for a question.
 */
export const useQuestionAnswerDisplayGroupings = <T = QuestionDisplayOption[]>(
  id: number | string,
  options: UseQueryOptions<QuestionDisplayOption[], unknown, T> = {},
) =>
  useQuery<QuestionDisplayOption[], unknown, T>(
    queryKeys.answerDisplayGroupings(id),
    () => QuestionsAPI.listDisplayGroupings(id),
    options,
  );

/**
 * Fetches display options for question scores.
 */
export const useQuestionScores = <T = QuestionDisplayOption[]>(
  id: number | string,
  options: UseQueryOptions<QuestionDisplayOption[], unknown, T> = {},
) =>
  useQuery<QuestionDisplayOption[], unknown, T>(
    queryKeys.scores(id),
    () => QuestionsAPI.listDisplayScores(id),
    options,
  );

/**
 * Fetches question insights based on the provided params.
 */
export const useQuestionInsightsList = <T = QuestionInsightListResponse>(
  args: QuestionInsightsListArgs,
  options: UseQueryOptions<QuestionInsightListResponse, unknown, T> = {},
) =>
  useQuery<QuestionInsightListResponse, unknown, T>(
    queryKeys.insightsList(args),
    () => QuestionsAPI.getInsightList(args),
    options,
  );

/**
 * Fetches question insights narrative based on the provided params.
 */
export const useQuestionInsightsNarrative = <T = QuestionInsightNarrativeResponse>(
  args: QuestionInsightsNarrativeArgs,
  options: UseQueryOptions<QuestionInsightNarrativeResponse, unknown, T> = {},
) =>
  useQuery<QuestionInsightNarrativeResponse, unknown, T>(
    queryKeys.insightsNarrative(args),
    () => QuestionsAPI.getInsightNarrative(args),
    options,
  );

/**
 * Performs a questions search using the provided params.
 */
export const useQuestionSearch = <T = SearchQuestionsResponse>(
  args: QuestionSearchArgs,
  options: UseQueryOptions<SearchQuestionsResponse, unknown, T> = {},
) => useQuery<SearchQuestionsResponse, unknown, T>(queryKeys.search(args), () => QuestionsAPI.search(args), options);

/**
 * Fetches timeview availability for a given question.
 */
export const useQuestionTimeviewStatus = <T = QuestionTimeviewStatus>(
  questionId: number | string,
  options: UseQueryOptions<QuestionTimeviewStatus, unknown, T> = {},
) =>
  useQuery<QuestionTimeviewStatus, unknown, T>(
    queryKeys.timeviewStatus(questionId),
    () => QuestionsAPI.getTimeviewStatus(questionId),
    options,
  );

/**
 * Fetch Score Results for chart data Timeview with Top Level Data.
 */
export const useQuestionScoreResults = <T = MultiScoreResults>(
  args: MultiScoreResultsArgs,
  options: UseQueryOptions<MultiScoreResults, unknown, T> = {},
) =>
  useQuery<MultiScoreResults, unknown, T>(
    queryKeys.scoreResults(args),
    () => QuestionsAPI.getScoreResults(args),
    options,
  );

/**
 * Fetch Results for checkbox question compare.
 */
export const useQuestionCompareCheckboxResults = (
  { qid, checkboxGroupId, filters: { segmentUUID, ...restFilters } }: useQuestionCompareCheckboxResultsArgs,
  options?: UseQueryOptions<CheckboxQuestionCompareCrosstabResponse>,
) => {
  const apiParams: GetQuestionCompareCheckboxResultsArgs = {
    ...restFilters,
    segments: [segmentUUID],
    qid,
    checkboxGroupId,
  };

  const cacheParams = {
    ...apiParams,
    ...cacheKeyForDateRange({ startDate: restFilters.startDate, endDate: restFilters.endDate }),
  };

  return useQuery<CheckboxQuestionCompareCrosstabResponse>(
    ["checkboxQuestionCompare", cacheParams],
    () => QuestionsAPI.getQuestionCompareCheckboxResults(apiParams),
    {
      ...defaultChartQueryOptions,
      ...options,
    },
  );
};

/**
 * Fetch a question.
 */
export const useQuestion = <T extends QuestionFull>(id: number, options: UseQueryOptions<QuestionFull, unknown, T>) =>
  useQuery<QuestionFull, unknown, T>(queryKeys.detail(id), () => QuestionsAPI.get(id, true), options);

/**
 * Mutation to update a question by ID.
 *
 * NOTE: This will automatically update the question in the cache on success.
 * It will also invalidate related question queries to force fetching fresh data
 * as editing a question (especially adding/removing answer choices) can impact downsream queries.
 */
export const useQuestionUpdate = (options: UseMutationOptions<QuestionFull, unknown, QuestionEditRequest> = {}) => {
  const queryClient = useQueryClient();
  return useMutation((data) => QuestionsAPI.edit(data), {
    ...options,
    onSuccess(data, variables, context) {
      // update the question in the cache
      queryClient.setQueryData(queryKeys.detail(data.questionId), data);

      // invalidate answer choices, question results, and display options
      queryClient.invalidateQueries(queryKeys.answerChoices(data.questionId));
      invalidateQuestionResults(queryClient, data.questionId);
      invalidateDisplayOptions(queryClient, data.questionId);

      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Invalidate the display options query for a question.
 * To be used after a mutation may have changed or created
 * the available options.
 */
export const invalidateDisplayOptions = (queryClient: QueryClient, questionId: number) =>
  queryClient.invalidateQueries({
    queryKey: queryKeys.answerDisplayOptions(questionId),
  });

/**
 * Fetch display options for a question.
 */
export const useQuestionDisplayOptions = <T = DisplayOptionsForQuestion>(
  id: number | string,
  options: UseQueryOptions<DisplayOptionsForQuestion, unknown, T> = {},
) =>
  useQuery<DisplayOptionsForQuestion, unknown, T>(
    queryKeys.answerDisplayOptions(id),
    () => QuestionsAPI.listDisplayOptions(id),
    options,
  );

/**
 * Fetch a question translation.
 */
export const useQuestionTranslation = <T extends QuestionTranslation>(
  id: number,
  locale: string,
  options: UseQueryOptions<QuestionTranslation, unknown, T>,
) =>
  useQuery<QuestionTranslation, unknown, T>(
    queryKeys.translation({ id, locale }),
    () => QuestionsAPI.getTranslation(id, locale),
    options,
  );

/**
 * Fetch question translations.
 */
export const useQuestionTranslations = <T extends QuestionTranslation[]>(
  id: number,
  options: UseQueryOptions<QuestionTranslation[], unknown, T>,
) =>
  useQuery<QuestionTranslation[], unknown, T>(
    queryKeys.translations(id),
    () => QuestionsAPI.listTranslations(id),
    options,
  );

/**
 * Deploy new question sample.
 */
export const useQuestionDeploy = (
  id: number,
  options: UseMutationOptions<QuestionSample, unknown, QuestionDeployRequest> = {},
) => useMutation((data) => QuestionsAPI.deployQuestion(id, data), options);

/**
 * Fetch the deployment targets associated to the provided question ID.
 */
export const useQuestionTargetsList = <T = QuestionDeployTarget[]>(
  questionId: number,
  options: UseQueryOptions<QuestionDeployTarget[], unknown, T> = {},
) =>
  useQuery<QuestionDeployTarget[], unknown, T>(
    queryKeys.targetList(questionId),
    () => QuestionsAPI.listTargets(questionId),
    options,
  );

/**
 * Mutation to change the approval/review of a question target.
 *
 * NOTE: This will automatically update the cache for the target list on success.
 */
export const useQuestionTargetUpdateApproval = (
  id: number,
  { onSuccess, ...options }: UseMutationOptions<QuestionDeployTarget, unknown, QuestionDeployTarget> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation((data) => QuestionsAPI.updateQuestionTargetReview(id, data), {
    ...options,
    onSuccess: (data, variables, context) => {
      /**
       * Replace the target in the cache with the new data.
       */
      queryClient.setQueryData<QuestionDeployTarget[]>(queryKeys.targetList(id), (oldData = []) =>
        mapBy(
          oldData,
          (x) => x.targetId === data.targetId,
          () => data,
        ),
      );

      if (onSuccess) {
        onSuccess(data, variables, context);
      }
    },
  });
};

type QuestionFavoriteToggleArgs = {
  id: string | number;
  /**
   * True to mark as favorite, false to remove from favorites.
   */
  isFavorite: boolean;
};

/**
 * Mutation to toggle the favorite status of a radio question.
 */
export const useQuestionFavoriteToggle = (
  options: UseMutationOptions<boolean, unknown, QuestionFavoriteToggleArgs> = {},
) => {
  return useMutation(async ({ id, isFavorite }: QuestionFavoriteToggleArgs) => {
    isFavorite ? QuestionsAPI.favorite(id) : QuestionsAPI.unfavorite(id);
    return isFavorite;
  }, options);
};

/**
 * Fetch the tags associated to the provided question ID.
 */
export const useQuestionTags = <T = QuestionTagListResponse>(
  questionId: number,
  options: UseQueryOptions<QuestionTagListResponse, unknown, T> = {},
) =>
  useQuery<QuestionTagListResponse, unknown, T>(
    queryKeys.tags(questionId),
    () => QuestionsAPI.listTags(questionId),
    options,
  );

/**
 * Add a new tag to a question by question ID.
 * Automatically invalidates the question tags in the cache on success.
 */
export const useQuestionTagCreate = (
  questionId: string | number,
  options: UseMutationOptions<void, unknown, string | number> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation((tagId) => QuestionsAPI.createTag(questionId, tagId), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.tags(questionId));
      options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Deletes a tag from a question by question ID.
 * Automatically invalidates the question tags in the cache on success.
 */
export const useQuestionTagDelete = (
  questionId: string | number,
  options: UseMutationOptions<void, unknown, string | number> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation((tagId) => QuestionsAPI.deleteTag(questionId, tagId), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.tags(questionId));
      options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Fetch the comments associated to the provided question ID.
 */
export const useQuestionComments = <T = QuestionComment[]>(
  questionId: number,
  options: UseQueryOptions<QuestionComment[], unknown, T> = {},
) =>
  useQuery<QuestionComment[], unknown, T>(
    queryKeys.comments(questionId),
    () => QuestionsAPI.listComments(questionId),
    options,
  );

/**
 * Creates a new comment for a question by question ID.
 * Automatically invalidates the question comments in the cache on success.
 */
export const useQuestionCommentCreate = (
  questionId: string | number,
  options: UseMutationOptions<QuestionComment, unknown, string> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation((comment) => QuestionsAPI.createComment(questionId, { comment }), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.comments(questionId));
      options.onSuccess?.(data, variables, context);
    },
  });
};

export const useQuestionNotifications = <T = QuestionNotification[]>(
  questionId: number,
  options: UseQueryOptions<QuestionNotification[], unknown, T> = {},
) =>
  useQuery<QuestionNotification[], unknown, T>(
    queryKeys.notifications(questionId),
    () => QuestionsAPI.listNotifications(questionId),
    options,
  );

export const useUpdateQuestionNotifications = (
  questionId: number,
  options: UseMutationOptions<QuestionNotification[], unknown, QuestionNotificationsRequest> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation((req) => QuestionsAPI.updateNotifications(questionId, req), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.notifications(questionId));
      options.onSuccess?.(data, variables, context);
    },
  });
};
